import { colors, radius, typography } from "@carrier/brand/tokens";
import { isEmptyScreen, type Bar, type Screen, type Section, type Tone } from "./model.js";

/**
 * Render a screen as a self-contained HTML panel for an MCP App.
 *
 * Self-contained is a hard requirement: the panel runs in a sandboxed iframe
 * with no network access, so every style is inline and there are no external
 * fonts, scripts or images. Colours come from `@carrier/brand` rather than
 * being retyped, which is the whole reason the previous hand-rolled panels
 * looked like three unrelated products.
 */

const TONE_COLOR: Record<Tone, string> = {
  ok: colors.statusSuccess,
  info: colors.accentFlame,
  warn: colors.statusWarning,
  critical: colors.statusError,
  muted: colors.textMuted,
};

/** Escape text for HTML. Every value on a panel passes through here. */
export function esc(value: unknown): string {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// `fallback` is widened to string: the brand tokens are `as const`, so an
// inferred default would narrow this to one literal hex and reject every other.
function toneColor(tone: Tone | undefined, fallback: string = colors.textPrimary): string {
  return tone ? TONE_COLOR[tone] : fallback;
}

function metricsHtml(items: { label: string; value: string | number; hint?: string; tone?: Tone }[]): string {
  const cards = items
    .map(
      (m) => `
      <div style="background:${colors.surface1};border:1px solid ${colors.borderCard};border-radius:${radius.lg};padding:16px 18px;min-width:0">
        <div style="font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:${colors.textMuted}">${esc(m.label)}</div>
        <div style="font-size:28px;font-weight:600;margin-top:6px;color:${toneColor(m.tone)};line-height:1.1">${esc(m.value)}</div>
        ${m.hint ? `<div style="font-size:12px;color:${colors.textFaint};margin-top:4px">${esc(m.hint)}</div>` : ""}
      </div>`,
    )
    .join("");
  return `<div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:12px">${cards}</div>`;
}

function barsHtml(items: Bar[], empty?: string): string {
  if (items.length === 0) return emptyHtml(empty ?? "Nothing to show.");
  const ceiling = Math.max(...items.map((b) => b.max ?? b.value), 1);
  const rows = items
    .map((b) => {
      const pct = Math.max(0, Math.min(100, ((b.value / (b.max ?? ceiling)) || 0) * 100));
      return `
      <div style="margin-bottom:10px">
        <div style="display:flex;justify-content:space-between;font-size:13px;color:${colors.textSecondary};margin-bottom:4px">
          <span>${esc(b.label)}</span>
          <span style="color:${colors.textMuted}">${esc(b.hint ?? b.value)}</span>
        </div>
        <div style="height:8px;background:${colors.surface2};border-radius:${radius.full};overflow:hidden">
          <div style="height:100%;width:${pct.toFixed(1)}%;background:${toneColor(b.tone, colors.accentFlame)}"></div>
        </div>
      </div>`;
    })
    .join("");
  return `<div>${rows}</div>`;
}

function tableHtml(section: Extract<Section, { kind: "table" }>): string {
  if (section.rows.length === 0) return emptyHtml(section.empty ?? "No rows.");
  const numeric = new Set(section.numeric ?? []);
  const head = section.columns
    .map(
      (c, i) =>
        `<th style="text-align:${numeric.has(i) ? "right" : "left"};padding:8px 12px;font-size:11px;letter-spacing:.08em;text-transform:uppercase;color:${colors.textMuted};border-bottom:1px solid ${colors.borderCard};white-space:nowrap">${esc(c)}</th>`,
    )
    .join("");
  const body = section.rows
    .map(
      (row) =>
        `<tr>${row
          .map(
            (cell, i) =>
              `<td style="text-align:${numeric.has(i) ? "right" : "left"};padding:8px 12px;font-size:13px;color:${colors.textSecondary};border-bottom:1px solid ${colors.borderSubtle};white-space:nowrap">${esc(cell)}</td>`,
          )
          .join("")}</tr>`,
    )
    .join("");
  // Wide tables scroll inside their own container; the page never scrolls sideways.
  return `<div style="overflow-x:auto"><table style="width:100%;border-collapse:collapse">${
    `<thead><tr>${head}</tr></thead>`
  }<tbody>${body}</tbody></table></div>`;
}

function keyValueHtml(items: { label: string; value: string; tone?: Tone }[]): string {
  const rows = items
    .map(
      (kv) => `
      <div style="display:flex;justify-content:space-between;gap:16px;padding:7px 0;border-bottom:1px solid ${colors.borderSubtle}">
        <span style="font-size:13px;color:${colors.textMuted}">${esc(kv.label)}</span>
        <span style="font-size:13px;color:${toneColor(kv.tone, colors.textPrimary)};text-align:right">${esc(kv.value)}</span>
      </div>`,
    )
    .join("");
  return `<div>${rows}</div>`;
}

function noteHtml(tone: Tone, text: string): string {
  const c = toneColor(tone, colors.accentFlame);
  return `<div style="border-left:3px solid ${c};background:${colors.surface1};padding:10px 14px;border-radius:${radius.md};font-size:13px;color:${colors.textSecondary}">${esc(text)}</div>`;
}

function emptyHtml(text: string): string {
  return `<div style="padding:14px;border:1px dashed ${colors.borderCard};border-radius:${radius.md};font-size:13px;color:${colors.textMuted}">${esc(text)}</div>`;
}

function sectionHtml(section: Section): string {
  const title = "title" in section && section.title
    ? `<h2 style="font-size:13px;font-weight:600;letter-spacing:.04em;color:${colors.textPrimary};margin:0 0 10px">${esc(section.title)}</h2>`
    : "";
  let body: string;
  switch (section.kind) {
    case "metrics":
      body = metricsHtml(section.items);
      break;
    case "bars":
      body = barsHtml(section.items, section.empty);
      break;
    case "table":
      body = tableHtml(section);
      break;
    case "keyvalue":
      body = keyValueHtml(section.items);
      break;
    case "note":
      body = noteHtml(section.tone, section.text);
      break;
  }
  return `<section style="margin-bottom:22px">${title}${body}</section>`;
}

/** Render a complete, self-contained HTML document for a screen. */
export function renderHtml(screen: Screen): string {
  const sections = isEmptyScreen(screen)
    ? emptyHtml(
        "No data came back for this view. That is a real result, not a loading state — check the account scope and credentials.",
      )
    : screen.sections.map(sectionHtml).join("");

  const actions = screen.actions?.length
    ? `<section style="margin-top:6px;display:flex;flex-wrap:wrap;gap:8px">${screen.actions
        .map(
          (a) =>
            `<span title="${esc(a.description ?? a.command)}" style="font-size:12px;color:${colors.textSecondary};background:${colors.surface2};border:1px solid ${colors.borderCard};border-radius:${radius.full};padding:6px 12px">${esc(a.label)} <code style="color:${colors.textFaint}">${esc(a.command)}</code></span>`,
        )
        .join("")}</section>`
    : "";

  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<title>${esc(screen.title)}</title></head>
<body style="margin:0;background:${colors.surface0};color:${colors.textPrimary};font-family:${typography.fontSans};padding:20px">
  <header style="margin-bottom:20px">
    <h1 style="font-size:18px;font-weight:600;margin:0;letter-spacing:-.01em">${esc(screen.title)}</h1>
    ${screen.subtitle ? `<p style="margin:4px 0 0;font-size:13px;color:${colors.textMuted}">${esc(screen.subtitle)}</p>` : ""}
  </header>
  ${sections}
  ${actions}
  ${screen.footer ? `<footer style="margin-top:18px;font-size:11px;color:${colors.textFaint}">${esc(screen.footer)}</footer>` : ""}
</body></html>`;
}
